import { api, getApiOrigin } from './api.js';
import type { AuthUser } from '../store/authStore.js';

/**
 * Converts an app-owned avatar URL (relative `/api/v1/users/:id/avatar?v=…`
 * returned by the API) to an absolute URL that can be used as `<img src>`.
 * Leaves absolute URLs (including http(s)) unchanged so provider CDNs still
 * render during the brief window before the server mirror completes.
 */
export function resolveAvatarSrc(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  const origin = getApiOrigin();
  if (!origin) return url;
  if (url.startsWith('/')) return `${origin}${url}`;
  return `${origin}/${url}`;
}

export async function uploadAvatar(file: File): Promise<AuthUser> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await api.post<{ user: AuthUser }>('/auth/me/avatar', form, {
    // Let axios/browser set the proper multipart boundary.
    // IMPORTANT: our shared axios instance defaults to JSON; remove it for FormData.
    transformRequest: (data, headers) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (headers as any)?.['Content-Type'];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (headers as any)?.['content-type'];
      return data;
    },
  });
  return res.data.user;
}

export async function deleteAvatar(): Promise<AuthUser> {
  const res = await api.delete<{ user: AuthUser }>('/auth/me/avatar');
  return res.data.user;
}

export async function mirrorAvatarFromUrl(remoteUrl: string): Promise<AuthUser> {
  const res = await api.post<{ user: AuthUser }>('/auth/me/avatar/mirror', { remoteUrl });
  return res.data.user;
}
